import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SiteFooter } from '../../components/SiteFooter'
import { Hanko } from '../../components/Hanko'
import { SiteHeader } from '../../components/SiteHeader'
import { Cartouche } from '../../components/lp/Cartouche'
import { Ignite, graphemes } from '../../components/lp/Ignite'
import { LiveStats } from '../../components/lp/LiveStats'
import { TimeDial } from '../../components/lp/TimeDial'
import { ReceiptView } from '../../components/receipt/ReceiptView'
import { CafeCanvas } from '../../components/scene/CafeCanvas'
import { useLocale } from '../../components/use-locale'
import { useTimeOfDay } from '../../components/use-time-of-day'
import { getDict, isCjk, localePath, parseLocale } from '../../i18n'
import { localHour } from '../../lib/date'
import { SEAT_ORDER } from '../../lib/seats'
import { seoHead } from '../../lib/seo'
import { SITE_ORIGIN } from '../../lib/site'
import { type TimeOfDay, timeOfDayFromHour } from '../../lib/time-of-day'
import type { SceneState, SceneView } from '../../scene/cafe-scene'
import { getCafeStats } from '../../server-fns/stats'

export const Route = createFileRoute('/{-$locale}/')({
  validateSearch: (s: Record<string, unknown>): { tod?: string } =>
    typeof s.tod === 'string' ? { tod: s.tod } : {},
  beforeLoad: ({ params }) => {
    if (!parseLocale(params.locale)) throw notFound()
  },
  loader: async () => ({
    stats: await getCafeStats(),
    serverTod: timeOfDayFromHour(localHour(Date.now(), 'Asia/Tokyo')),
  }),
  head: ({ params }) => {
    const locale = parseLocale(params.locale) ?? 'ja'
    const t = getDict(locale)
    return seoHead({
      origin: SITE_ORIGIN,
      locale,
      path: '/',
      title: t.meta.lpTitle,
      description: t.meta.lpDescription,
      keywords: t.meta.lpKeywords,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: t.meta.siteName,
        url: SITE_ORIGIN + localePath(locale, '/'),
        description: t.meta.lpDescription,
        applicationCategory: 'ProductivityApplication',
        operatingSystem: 'Web',
        inLanguage: locale,
        keywords: t.meta.lpKeywords.join(','),
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'JPY' },
      },
    })
  },
  component: Landing,
})

// 誰もいない時間でも店が寂しく見えないよう、LP では最低限の灯りを入れる
const AMBIENT_SEATS = 14
// 物語の主役になる席（2階の窓際）。最初は消えていて、2章で灯る
const STORY_SEAT = 19

type Chapter = 'hero' | 'approach' | 'seat' | 'sky' | 'receipt' | 'times' | 'uses' | 'closing'

const SHOTS: Record<Chapter, SceneView> = {
  hero: 'exterior',
  approach: 'approach',
  seat: 'seat',
  sky: 'sky',
  receipt: 'exterior',
  times: 'exterior',
  uses: 'exterior',
  closing: 'sky',
}

const SAMPLE = {
  id: 'Fc7x2Lp9QaZ4',
  plannedMinutes: 25,
  focusSeconds: 1500,
  seat: 17,
}

/** 画面の中央を横切っている章を返す */
function useActiveChapter(): [Chapter, (el: HTMLElement | null) => void] {
  const [active, setActive] = useState<Chapter>('hero')
  const els = useRef(new Set<HTMLElement>())
  const io = useRef<IntersectionObserver | null>(null)
  useEffect(() => {
    io.current = new IntersectionObserver(
      (entries) => {
        for (const e of entries)
          if (e.isIntersecting) setActive((e.target as HTMLElement).dataset.chapter as Chapter)
      },
      { rootMargin: '-48% 0px -48% 0px' },
    )
    for (const el of els.current) io.current.observe(el)
    return () => io.current?.disconnect()
  }, [])
  const register = (el: HTMLElement | null) => {
    if (!el || els.current.has(el)) return
    els.current.add(el)
    io.current?.observe(el)
  }
  return [active, register]
}

function Landing() {
  const { stats, serverTod } = Route.useLoaderData()
  const { tod: todParam } = Route.useSearch()
  const { t, locale, path } = useLocale()
  const liveTod = useTimeOfDay(serverTod, todParam)
  const [pickedTod, setPickedTod] = useState<TimeOfDay | null>(null)
  const tod = pickedTod ?? liveTod
  const focusing = stats?.focusing ?? 0
  const [chapter, register] = useActiveChapter()

  // 3章に入った瞬間に、湯気が星になる演出を一度だけ流す
  const [celebrated, setCelebrated] = useState(0)
  useEffect(() => {
    if (chapter === 'sky') setCelebrated((n) => n + 1)
  }, [chapter])

  const storyLit = chapter === 'seat' || chapter === 'sky'
  const baseStars = Math.max(stats?.stars ?? 0, 64)
  const scene = useMemo<SceneState>(() => {
    const n = Math.min(48, Math.max(focusing, AMBIENT_SEATS))
    const seats = SEAT_ORDER.filter((s) => s !== STORY_SEAT)
      .slice(0, n)
      .map((seat, i) => ({ seat, status: i % 6 === 5 ? ('rest' as const) : ('focus' as const), mine: false }))
    if (storyLit) seats.push({ seat: STORY_SEAT, status: 'focus', mine: true })
    return {
      seats,
      timeOfDay: tod,
      stars: baseStars + (celebrated > 0 ? 1 : 0),
      mySeat: STORY_SEAT,
      view: SHOTS[chapter],
    }
  }, [focusing, tod, baseStars, chapter, storyLit, celebrated])

  const celebrate = useMemo(
    () => (celebrated > 0 ? { key: celebrated, seat: STORY_SEAT } : undefined),
    [celebrated],
  )

  // 見出しが一文字ずつ灯り終わる時刻（CSS の .ignite__ch の遅れと長さに合わせる）。3D はそれまで読み込まない
  const titleDoneMs =
    700 + (graphemes(t.lp.heroLine1).length + graphemes(t.lp.heroLine2).length - 1) * 95 + 2600

  const sample = useMemo(() => {
    const endedAt = Date.UTC(2026, 9, 12, 12, 30)
    return {
      ...SAMPLE,
      locale,
      startedAt: endedAt - 25 * 60_000,
      endedAt,
      timeZone: 'Asia/Tokyo',
      name: 'mio',
      note: getDict(locale).lp.sampleNote,
    }
  }, [locale])

  return (
    <div className={`lp lp--${chapter}`} data-tod={tod}>
      <SiteHeader />
      <div className="lp__stage">
        <CafeCanvas
          state={scene}
          celebrate={celebrate}
          className="lp__scene"
          label={t.meta.lpDescription}
          bootAfter={titleDoneMs}
        />
        <div className="lp__veil" aria-hidden="true" />
      </div>
      <main id="main" className="lp__story">
        <section className="hero" aria-labelledby="hero-title" data-chapter="hero" ref={register}>
          <Cartouche tod={tod} />
          <h1 id="hero-title" className="hero__title" aria-label={`${t.lp.heroLine1}${t.lp.heroLine2}`}>
            <Ignite text={t.lp.heroLine1} className="hero__line" />
            <Ignite text={t.lp.heroLine2} className="hero__line" delay={graphemes(t.lp.heroLine1).length} />
          </h1>
          <div className="hero__foot">
            <div className="hero__copy">
              <p className="hero__lead">{t.lp.heroLead}</p>
              <div className="hero__actions">
                <Link to={path('/cafe')} className="btn btn--lamp">
                  {t.lp.cta}
                </Link>
                <p className="hero__note">{t.lp.ctaNote}</p>
              </div>
            </div>
            <LiveStats stats={stats} />
          </div>
        </section>

        <section className="chapters" aria-labelledby="menu-title">
          <h2 id="menu-title" className="sr-only">
            {t.lp.stepsTitle}
          </h2>
          <ol className="chapters__list">
            {t.lp.steps.map((s, i) => {
              const id = (['approach', 'seat', 'sky'] as const)[i]
              return (
                <li key={s.title} className="chapter" data-chapter={id} ref={register}>
                  <div className="chapter__text">
                    <p className="chapter__num" aria-hidden="true">
                      <Hanko text={`其${['一', '二', '三'][i]}`} size={52} className="chapter__seal" />
                      {!isCjk(locale) && <span className="chapter__roman">{['I', 'II', 'III'][i]}</span>}
                    </p>
                    <h3 className="chapter__title">{s.title}</h3>
                    <p className="chapter__body">{s.body}</p>
                  </div>
                </li>
              )
            })}
          </ol>
        </section>

        <section className="lp-receipt" aria-labelledby="receipt-title" data-chapter="receipt" ref={register}>
          <div className="lp-receipt__text">
            <h2 id="receipt-title" className="lp-heading">
              {t.lp.receiptTitle}
            </h2>
            <p className="lp-body">{t.lp.receiptBody}</p>
          </div>
          <div className="lp-receipt__paper">
            <div className="lp-receipt__light" aria-hidden="true" />
            <ReceiptView data={sample} tilt={-2.5} />
          </div>
        </section>

        <section className="lp-times" aria-labelledby="times-title" data-chapter="times" ref={register}>
          <div className="lp-times__text">
            <h2 id="times-title" className="lp-heading">
              {t.lp.timeTitle}
            </h2>
            <p className="lp-body">{t.lp.timeBody}</p>
          </div>
          <TimeDial value={tod} onChange={setPickedTod} />
        </section>

        <section className="lp-uses" aria-labelledby="uses-title" data-chapter="uses" ref={register}>
          <div className="lp-uses__text">
            <h2 id="uses-title" className="lp-heading">
              {t.lp.usesTitle}
            </h2>
            <p className="lp-body">{t.lp.usesLead}</p>
          </div>
          <ul className="lp-uses__list">
            {t.lp.uses.map((u) => (
              <li key={u.title} className="lp-uses__item">
                <h3 className="lp-uses__title">{u.title}</h3>
                <p className="lp-uses__body">{u.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="lp-closing" aria-labelledby="closing-title" data-chapter="closing" ref={register}>
          <h2 id="closing-title" className="lp-closing__title">
            {t.lp.closingTitle}
          </h2>
          <Link to={path('/cafe')} className="btn btn--lamp">
            {t.lp.closingCta}
          </Link>
        </section>
      </main>
      <SiteFooter />
    </div>
  )
}
